// Tiện ích cho cấu trúc bài học theo PHẦN (hoạt động sư phạm).
import type { Card, LessonDoc, LessonSection, SectionKind } from '../types/LessonDoc'
import { genId } from './id'

export const DEFAULT_SECTION_TITLES = ['Khởi động', 'Hình thành kiến thức', 'Luyện tập', 'Vận dụng', 'Mở rộng']

// ── Kiểu phần chuẩn e-learning: icon + màu đặc trưng + gợi ý ──
export interface SectionKindMeta {
  kind: SectionKind
  label: string
  icon: string            // emoji (đơn giản, không cần ảnh)
  /** Lớp Tailwind cho chip/tab (nền nhạt + chữ) và dải màu (accent). */
  chip: string
  accent: string          // mã màu cho thanh/điểm nhấn
  hint: string            // gợi ý loại thẻ nên dùng
}

// Nhãn theo Công văn 5512: Khởi động → Hình thành kiến thức → Luyện tập → Vận dụng (+ Mở rộng).
export const SECTION_KINDS: Record<SectionKind, SectionKindMeta> = {
  warmup:      { kind: 'warmup',      label: 'Khởi động',            icon: '🔥', chip: 'bg-orange-50 text-orange-700 border-orange-200',  accent: '#f97316', hint: 'Tình huống thực tế chứa mâu thuẫn + mục tiêu bài học' },
  content:     { kind: 'content',     label: 'Hình thành kiến thức', icon: '📘', chip: 'bg-blue-50 text-blue-700 border-blue-200',      accent: '#2563eb', hint: 'Các Hoạt động I, II...: lý thuyết + câu hỏi nhỏ, mô phỏng, phiên trực tiếp' },
  video:       { kind: 'video',       label: 'Video',                icon: '🎬', chip: 'bg-rose-50 text-rose-700 border-rose-200',      accent: '#e11d48', hint: 'Video bài giảng / minh hoạ' },
  consolidate: { kind: 'consolidate', label: 'Luyện tập',            icon: '✅', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: '#059669', hint: 'Video YouTube ôn lại + Trắc nghiệm PMaker' },
  infographic: { kind: 'infographic', label: 'Vận dụng',             icon: '🌍', chip: 'bg-violet-50 text-violet-700 border-violet-200', accent: '#7c3aed', hint: 'Tìm hiểu hiện tượng liên quan, sơ đồ, infographic' },
  custom:      { kind: 'custom',      label: 'Phần học',             icon: '📄', chip: 'bg-slate-50 text-slate-700 border-slate-200',    accent: '#475569', hint: 'Nội dung tuỳ chỉnh (vd Mở rộng: Tiếng Anh, Game)' },
}

// Suy ra kiểu phần từ tiêu đề (cho bài cũ chưa gắn kind).
export function inferSectionKind(title: string): SectionKind {
  const t = (title || '').toLowerCase()
  if (/(mở đầu|khởi động|warm|dẫn nhập|tình huống)/.test(t)) return 'warmup'
  if (/(video|phim|clip)/.test(t)) return 'video'
  if (/(vận dụng|tìm hiểu thêm|infographic|sơ đồ|tư duy|tóm tắt|mindmap)/.test(t)) return 'infographic'
  if (/(củng cố|luyện tập|kết thúc|ôn tập|kiểm tra|trắc nghiệm)/.test(t)) return 'consolidate'
  if (/(nội dung|kiến thức|lý thuyết|hình thành|hoạt động chính)/.test(t)) return 'content'
  return 'content'
}

// Lấy meta kiểu phần (ưu tiên sec.kind, nếu không có thì suy từ tiêu đề).
export function sectionKindMeta(sec: { kind?: SectionKind; title?: string }): SectionKindMeta {
  const k = sec.kind && SECTION_KINDS[sec.kind] ? sec.kind : inferSectionKind(sec.title || '')
  return SECTION_KINDS[k]
}

export function makeSection(title: string, cards: Card[] = [], kind?: SectionKind): LessonSection {
  return { id: genId('sec'), title, kind, cards }
}

// Mẫu phần chuẩn Công văn 5512 cho bài MỚI: Khởi động → Hình thành kiến thức → Luyện tập → Vận dụng
// (+ Mở rộng: Tiếng Anh, Game — ngoài các hoạt động chính). GV xoá/thêm/đổi tên tuỳ ý; phần rỗng tự ẩn khi xem.
export function makeDefaultSections(): LessonSection[] {
  return [
    makeSection('Khởi động', [], 'warmup'),
    makeSection('Hình thành kiến thức', [], 'content'),
    makeSection('Luyện tập', [], 'consolidate'),
    makeSection('Vận dụng', [], 'infographic'),
    makeSection('Mở rộng', [], 'custom'),
  ]
}

// Chuẩn hoá doc đọc từ backend/localStorage: đảm bảo có sections hợp lệ.
// Tương thích ngược: doc cũ dạng { cards: [...] } → gói vào 1 phần "Hoạt động chính".
export function normalizeLesson(raw: any): LessonDoc {
  const doc: LessonDoc = { ...raw }
  let sections: LessonSection[] = Array.isArray(raw?.sections) ? raw.sections : []

  if (!sections.length && Array.isArray(raw?.cards) && raw.cards.length) {
    sections = [makeSection('Hoạt động chính', raw.cards)]
  }
  if (!sections.length) {
    sections = makeDefaultSections()
  }
  // Đảm bảo mỗi section có id + mảng cards (GIỮ kind để hiển thị đúng icon/màu, GIỮ lồng tiếng)
  doc.sections = sections.map((s) => ({
    id: s.id || genId('sec'),
    title: s.title || 'Phần học',
    kind: s.kind,
    cards: Array.isArray(s.cards) ? s.cards : [],
    audioUrl: (s as any).audioUrl,
    voiceScript: (s as any).voiceScript,
  }))
  delete (doc as any).cards
  return doc
}

// Đếm tổng số thẻ trong toàn bài (dùng cho hiển thị danh sách).
export function countCards(doc: any): number {
  if (Array.isArray(doc?.sections)) {
    return doc.sections.reduce((n: number, s: any) => n + (s.cards?.length || 0), 0)
  }
  return Array.isArray(doc?.cards) ? doc.cards.length : 0
}
